import { useState } from "react";
import { Brain, Database, Code, Shield, CheckCircle2, TrendingUp, Award, Globe, MessageCircle, Users, Sparkles, Star, Briefcase, ArrowRight } from "lucide-react";

export default function TalentExpertiseSplit() {
  const [activeTab, setActiveTab] = useState(0);

const domains = [
  {
    id: 0,
    title: "AI & Data Engineering",
    icon: Brain,
    color: "#0d9488",
    gradient: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)",
    lightBg: "#f0fdfa",
    description: "Transform raw data into actionable intelligence with cutting-edge AI solutions. From predictive analytics to computer vision, our engineers build scalable pipelines and machine learning models that drive business innovation. We help organizations harness the power of generative AI, recommendation engines, and real-time analytics to uncover hidden patterns, automate complex decisions, and create personalized customer experiences that deliver measurable ROI.",
    roles: [
      { name: "AI/ML Engineers", desc: "Neural networks, NLP models, and computer vision deployment" },
      { name: "Data Engineers", desc: "Data warehousing, ETL pipelines, Spark, and big data architecture" },
      { name: "QA Engineers", desc: "Automated regression, performance testing, and quality assurance" }
    ]
  },
  {
    id: 1,
    title: "SAP & Enterprise ERP",
    icon: Database,
    color: "#0058a9",
    gradient: "linear-gradient(135deg, #0058a9 0%, #1a7bc1 100%)",
    lightBg: "#e8f2fa",
    description: "Scale your business operations with world-class ERP implementation, configuration, and project management experts. Our certified professionals ensure seamless digital transformation across finance, supply chain, HR, and procurement modules. We specialize in end-to-end SAP S/4HANA migrations, system optimizations, and custom development that align with your unique business processes, helping you reduce operational costs while improving data visibility and decision-making speed.",
    roles: [
      { name: "SAP ERP Consultants", desc: "S/4HANA migrations, ABAP coding, and functional setup" },
      { name: "Project Managers", desc: "Agile delivery, scrum mastering, and enterprise program leadership" },
      { name: "IT Consultants", desc: "ERP advisory, systems integration, and functional alignment" }
    ]
  },
    {
    id: 2,
    title: "Infor & Industry ERP",
    icon: Database, // Replace with appropriate icon
    color: "#e11d48",
    gradient: "linear-gradient(135deg, #e11d48 0%, #fb7185 100%)",
    lightBg: "#fff0f3",
    description: "Maximize operational efficiency with specialized Infor ERP solutions tailored for manufacturing, healthcare, distribution, and hospitality. Our certified team delivers end-to-end implementation, customization, and support for Infor CloudSuite, M3, LN, and SyteLine. From supply chain optimization to asset management and financial controls, we help industry leaders streamline complex workflows, enhance visibility across global operations, and accelerate time-to-value with rapid deployment methodologies.",
    roles: [
      { name: "Infor Consultants", desc: "CloudSuite, M3, LN, and SyteLine implementation" },
      { name: "Technical Specialists", desc: "Mongoose development, ION integration, and Birst reporting" },
      { name: "Industry Advisors", desc: "Manufacturing, healthcare, and distribution domain expertise" }
    ]
  },
  {
    id: 3,
    title: "Odoo & Open Source ERP",
    icon: Database, // Replace with appropriate icon
    color: "#714b67",
    gradient: "linear-gradient(135deg, #714b67 0%, #8a6e7d 100%)",
    lightBg: "#f7f3f6",
    description: "Empower your business with flexible, cost-effective Odoo solutions that scale from startups to enterprises. Our Odoo experts deliver end-to-end implementation, customization, and integration across all modules including sales, CRM, inventory, accounting, manufacturing, and e-commerce. We leverage the power of Odoo's modular architecture to build tailored solutions that automate workflows, provide real-time business insights, and grow seamlessly with your evolving needs—all at a fraction of traditional ERP costs.",
    roles: [
      { name: "Odoo Developers", desc: "Custom module development, Python backend, and XMLRPC APIs" },
      { name: "Functional Consultants", desc: "Business process mapping, workflow automation, and user training" },
      { name: "Integration Specialists", desc: "Third-party APIs, payment gateways, and e-commerce sync" }
    ]
  },
  {
    id: 4,
    title: "Web & Application Development",
    icon: Database, // Replace with appropriate icon like Globe or Code
    color: "#d97706",
    gradient: "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)",
    lightBg: "#fff7ed",
    description: "Build stunning, high-performance digital experiences with our full-stack web and application development experts. From responsive SPAs to native mobile apps, we deliver scalable solutions using modern frameworks like React, Vue, Angular, Flutter, and React Native. Our agile teams specialize in e-commerce platforms, PWAs, dashboards, and custom enterprise portals that prioritize speed, accessibility, and SEO. We bridge design vision with robust backend architecture to create products users love.",
    roles: [
      { name: "Frontend Developers", desc: "React, Vue, Angular, TypeScript, and responsive design" },
      { name: "Backend Engineers", desc: "Node.js, Python, Java, .NET, and RESTful APIs" },
      { name: "Mobile Developers", desc: "Flutter, React Native, iOS Swift, and Android Kotlin" }
    ]
  },
  {
    id: 5,
    title: "QA & Test Automation",
    icon: Shield, // Replace with appropriate icon like CheckCircle
    color: "#0066a1",
    gradient: "linear-gradient(135deg, #0066a1 0%, #0086c5 100%)",
    lightBg: "#eef4fa",
    description: "Deliver flawless software with enterprise-grade quality assurance and test automation strategies. Our QA engineers combine manual precision with automated frameworks to ensure zero-defect releases across web, mobile, and desktop applications. We implement continuous testing pipelines, performance benchmarking, security scanning, and regression suites that cut testing time by up to 70%. From agile sprints to legacy system validation, we guarantee reliability, scalability, and exceptional user experiences.",
    roles: [
      { name: "QA Automation Engineers", desc: "Selenium, Cypress, Playwright, and Appium frameworks" },
      { name: "Performance Testers", desc: "JMeter, LoadRunner, and scalability benchmarking" },
      { name: "Manual QA Analysts", desc: "Test case design, exploratory testing, and UAT coordination" }
    ]
  },
  {
    id: 6,
    title: "Cloud & Security",
    icon: Shield,
    color: "#7c3aed",
    gradient: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
    lightBg: "#f5f3ff",
    description: "Secure your digital infrastructure with zero-trust architecture and multi-cloud expertise. Our specialists ensure high availability, disaster recovery, and compliance with industry standards like ISO, SOC2, and GDPR. We design and implement robust cloud-native solutions across AWS, Azure, and Google Cloud, combining cutting-edge DevSecOps practices with proactive threat detection to protect your assets from evolving cyber threats while maintaining optimal performance and scalability.",
    roles: [
      { name: "Cloud Engineers", desc: "AWS, Azure, and Google Cloud hosting and cost optimization" },
      { name: "Cybersecurity Specialists", desc: "Penetration testing, compliance auditing, and security protocols" },
      { name: "Network Administrators", desc: "Network security, router configs, and infrastructure routing" }
    ]
  }
];

  const activeDomain = domains[activeTab];
  const Icon = activeDomain.icon;

  return (
    <section
      className="w-full py-20 md:py-28 lg:py-16"
      style={{ backgroundColor: "#fafbfc" }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        
        {/* Header - Enhanced */}
        <div className="text-center mb-16">

          <h2
            className="text-4xl md:text-5xl font-semibold leading-tight mb-4"
            style={{ 
              fontFamily: 'Outfit, sans-serif',
              color: '#081120'
            }}
          >
            Specialized Talent Across{' '}
            <span className="relative inline-block">
              Cutting-Edge Domains
              <svg className="absolute -bottom-2 left-0 w-full h-3 -z-10" viewBox="0 0 200 10" preserveAspectRatio="none">
                <path d="M0 8 L200 8" stroke="#0058a9" strokeWidth="3" strokeLinecap="round" strokeDasharray="6 6" opacity="0.3"/>
              </svg>
            </span>
          </h2>

          <p
            className="max-w-3xl mx-auto text-lg md:text-xl leading-relaxed"
            style={{ 
              fontFamily: '"Open Sans", sans-serif',
              color: '#6b7280'
            }}
          >
            Access pre-vetted professionals with deep expertise in emerging and enterprise technologies
          </p>
        </div>

        {/* Tab Navigation - Enhanced */}
        <div className="flex flex-wrap justify-center gap-3 mb-16">
          {domains.map((domain, idx) => {
            const DomainIcon = domain.icon;
            const isActive = activeTab === idx;
            return (
              <button
                key={idx}
                onClick={() => setActiveTab(idx)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full transition-all duration-300"
                style={{
                  backgroundColor: isActive ? domain.color : '#ffffff',
                  color: isActive ? 'white' : '#6b7280',
                  border: isActive ? 'none' : '1px solid #e5e7eb',
                  transform: isActive
                    ? 'translateY(-2px) scale(1.03)'
                    : 'translateY(0) scale(1)',

                  boxShadow: isActive
                    ? `0 8px 24px ${domain.color}30`
                    : '0 1px 2px rgba(0,0,0,0.05)',

                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                <DomainIcon className="w-4 h-4" />
                <span 
                  className="text-sm font-medium"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  {domain.title.split(' & ')[0]}
                </span>
              </button>
            );
          })}
        </div>

        {/* Main Content Area */}
        <div
          key={activeTab}
          className="grid lg:grid-cols-2 gap-8 lg:gap-12 tab-content"
        >
          
          {/* Left Column - Enhanced Card */}
          <div>
            <div 
              className="rounded-2xl p-8 transition-all duration-300 h-full"
              style={{ 
                backgroundColor: '#ffffff',
                border: `1px solid ${activeDomain.color}20`,
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}
            >
              {/* Icon & Title */}
              <div className="flex items-center gap-4 mb-6">
                <div className="relative">
                  <div 
                    className="absolute inset-0 rounded-2xl blur-md transition-all duration-300"
                    style={{ 
                      background: activeDomain.gradient,
                      opacity: 0.2
                    }}
                  />
                  <div 
                    className="relative w-16 h-16 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: activeDomain.lightBg }}
                  >
                    <Icon className="w-8 h-8" style={{ color: activeDomain.color }} />
                  </div>
                </div>
                <div>
                  <h3 
                    className="text-2xl font-bold"
                    style={{ 
                      fontFamily: 'Outfit, sans-serif',
                      color: '#081120'
                    }}
                  >
                    {activeDomain.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <TrendingUp className="w-3.5 h-3.5" style={{ color: activeDomain.color }} />
                    <span 
                      className="text-xs"
                      style={{ fontFamily: '"Open Sans", sans-serif', color: '#6b7280' }}
                    >
                      High-demand expertise
                    </span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <p 
                className="text-base leading-relaxed mb-8"
                style={{ 
                  fontFamily: '"Open Sans", sans-serif',
                  color: '#6b7280',
                   wordBreak: 'normal',overflowWrap: 'normal',hyphens: 'none',
                }}
              >
                {activeDomain.description}
              </p>

              {/* Chat button */}
              {/* <div 
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 cursor-pointer group"
                style={{ 
                  background: activeDomain.gradient,
                  transform: 'translateY(0)'
                }}
                  className="group flex items-start gap-3 p-4 rounded-xl transition-all duration-300 hover:translate-x-1"
              >
                <MessageCircle className="w-4 h-4" style={{ color: '#ffffff' }} />
                <span 
                  className="text-sm font-medium"
                  style={{ fontFamily: '"Open Sans", sans-serif', color: '#ffffff' }}
                >
                  Chat with us 👋
                </span>
              </div> */}
            </div>
          </div>

          {/* Right Column - Roles List */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-5 h-5" style={{ color: activeDomain.color }} />
              <h4 
                className="font-semibold"
                style={{ 
                  fontFamily: 'Outfit, sans-serif',
                  color: '#081120'
                }}
              >
                Key Roles We Fill
              </h4>
            </div>

            <div className="space-y-4">
              {activeDomain.roles.map((role, idx) => (
                <div 
                  key={idx}
                  className="group flex items-start gap-3 p-4 rounded-xl transition-all duration-300"
                  style={{ 
                    backgroundColor: '#ffffff',
                    border: `1px solid ${activeDomain.color}15`,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = `${activeDomain.color}40`;
                    e.currentTarget.style.boxShadow = `0 4px 12px ${activeDomain.color}10`;
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = `${activeDomain.color}15`;
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110"
                    style={{ backgroundColor: activeDomain.lightBg }}
                  >
                    <CheckCircle2 className="w-4 h-4" style={{ color: activeDomain.color }} />
                  </div>
                  <div>
                    <h5 
                      className="font-semibold mb-1"
                      style={{ 
                        fontFamily: 'Outfit, sans-serif',
                        color: '#081120'
                      }}
                    >
                      {role.name}
                    </h5>
                    <p 
                      className="text-sm leading-relaxed"
                      style={{ 
                        fontFamily: '"Open Sans", sans-serif',
                        color: '#6b7280'
                      }}
                    >
                      {role.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <style>
          {
            `
            @keyframes fadeSlide {
            from {
              opacity: 0;
              transform: translateY(12px);
            }

            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .tab-content {
            animation: fadeSlide 0.4s ease-out;
          }
            `
          }
        </style>
      </div>
    </section>
  );
}